import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../api";
import { listCoaches } from "../../../api/users";
import { fetchBranches } from "../../../api/branches";
import DatePicker from '../../../components/common/DatePicker';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import ReportExportBar from '../../../components/reports/ReportExportBar';
import ReportGenerateButton from '../../../components/reports/ReportGenerateButton';
import { exportCoachRevenueReport, emailCoachRevenueReport } from '../../../api/adminReports';
import { getActiveRole } from '../../../utils/auth';
import PageHeaderSection from '../../../components/layout/PageHeaderSection';
import { ListLoadingSpinner } from '../../../components/layout/ListPageLayout';
import { formatCurrency } from '../../../utils/currency';
import { useReportSettings } from '../../../context/ReportSettingsContext';
import { SelectInput } from '../../../components/common/form';
import SessionDateTimeDisplay from '../../../components/common/SessionDateTimeDisplay';
import { localizeBatchName, studentDisplayName } from '../../../utils/displayNames';
import {
  ChartPanel,
  TrendLine,
  CompareBar,
  CompositionDonut,
  CHART_COLORS,
} from '../../../components/charts';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatDate, formatTime } from '../../../utils/regionalFormat';
import {
  firstDayOfMonthRegional,
  todayRegionalDate,
  toISODateFromRegional,
  getDateFormatPlaceholder,
} from '../../../utils/date';

function displaySessionDate(session) {
  return formatDate(session.sessionDate) || session.sessionDate || '—';
}

function displaySessionTime(value) {
  return formatTime(value) || value || '—';
}

function getFirstDayOfMonth() {
  return firstDayOfMonthRegional();
}

function getToday() {
  return todayRegionalDate();
}

function ddmmyyyyToIso(str) {
  const iso = toISODateFromRegional(str);
  return iso || null;
}

function AttendancePopup({ session, onClose }) {
  const { t } = useTranslation();

  if (!session || !session.attendance) return null;

  const present = session.attendance.filter(a => a.status === 'PRESENT');
  const absent = session.attendance.filter(a => a.status === 'ABSENT');

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />

      <div
        className="fixed z-50 flex flex-col bg-white border-2 border-gray-300 rounded-lg shadow-2xl w-full max-w-md max-h-[85vh]"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        role="dialog"
        aria-labelledby="session-attendance-title"
      >
        <div className="flex-shrink-0 flex justify-between items-start p-4 pb-3 border-b">
          <div>
            <div id="session-attendance-title" className="font-semibold text-gray-800">📋 {t('reports.common.sessionAttendance')}</div>
            <div className="text-xs text-gray-500">{t('reports.common.sessionId')} #{session.sessionId} - {displaySessionDate(session)}</div>
            <div className="text-xs text-blue-600 font-medium mt-1">🕐 {displaySessionTime(session.startTime)} - {displaySessionTime(session.endTime)}</div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            title={t('reports.common.close')}
            type="button">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-3">
          {present.length > 0 && (
            <div>
              <div className="text-sm font-medium text-green-700 mb-2 sticky top-0 bg-white py-1">✅ {t('reports.common.presentCount', { count: present.length })}</div>
              <div className="space-y-1">
                {present.map((att, idx) => (
                  <div key={idx} className="text-xs text-gray-700 flex justify-between items-center py-1 gap-2">
                    <span className="flex-1 text-left">• {studentDisplayName(att.studentName, null, t)}</span>
                    <span className="text-green-600 font-medium text-right shrink-0">
                      {att.amountBilled > 0 ? formatCurrency(att.amountBilled) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {absent.length > 0 && (
            <div>
              <div className="text-sm font-medium text-red-700 mb-2 sticky top-0 bg-white py-1">❌ {t('reports.common.absentCount', { count: absent.length })}</div>
              <div className="space-y-1">
                {absent.map((att, idx) => (
                  <div key={idx} className="text-xs text-gray-700 flex justify-between items-center py-1 gap-2">
                    <span className="flex-1 text-left">• {studentDisplayName(att.studentName, null, t)}</span>
                    <span className="text-red-600 font-medium text-right shrink-0">
                      {att.amountBilled > 0 ? formatCurrency(att.amountBilled) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-4 py-3 border-t text-sm bg-gray-50 rounded-b-lg">
          <span className="font-medium text-gray-700">{t('reports.common.attendanceRate')}: </span>
          <span className="font-bold text-blue-600">
            {session.totalStudents > 0 ? Math.round((session.presentCount / session.totalStudents) * 100) : 0}%
          </span>
        </div>
      </div>
    </>
  );
}

export default function CoachRevenueReport({ embeddedInHub = false }) {
  const { t } = useTranslation();
  const { settings: reportSettings } = useReportSettings();
  const batchAcademyPct = reportSettings?.batchAcademyCommissionPercent ?? 30;
  const individualAcademyPct = reportSettings?.individualAcademyCommissionPercent ?? 20;

  const [filters, setFilters] = useState({
    from: getFirstDayOfMonth(),
    to: getToday(),
    coachId: "",
    branchId: ""
  });

  const [coaches, setCoaches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [clickedSession, setClickedSession] = useState(null);
  const [filtersLoadError, setFiltersLoadError] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    (async () => {
      let failed = false;
      try {
        const c = await listCoaches();
        setCoaches(c || []);
      } catch (e) {
        console.error("Failed to load coaches:", e);
        setCoaches([]);
        failed = true;
      }
      try {
        const br = await fetchBranches();
        setBranches((br && br.data) || []);
      } catch (e) {
        console.error("Failed to load branches:", e);
        setBranches([]);
        failed = true;
      }
      setFiltersLoadError(failed);
    })();
  }, []);

  // Regenerate report when page size changes
  useEffect(() => {
    if (hasRun && data) {
      generateReport(0); // Reset to first page when page size changes
    }
  }, [pageSize]);

  // After a page loads, bring the session table back under the sticky header.
  useEffect(() => {
    if (loading || !data?.sessions?.length) return;
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, data?.currentPage, pageSize]);

  const generateReport = async (page = 0) => {
    setErr("");
    if (!filters.from || !filters.to) {
      setErr(t('reports.common.selectDateRange'));
      return;
    }

    const fromIso = ddmmyyyyToIso(filters.from);
    const toIso = ddmmyyyyToIso(filters.to);

    if (!fromIso || !toIso) {
      setErr(t('reports.common.invalidDateFormat', { format: getDateFormatPlaceholder() }));
      return;
    }

    setLoading(true);
    setHasRun(true);
    setCurrentPage(page);

    try {
      const params = {
        from: fromIso,
        to: toIso,
        page: page,
        size: pageSize
      };

      // Only add coachId if it's a valid number
      if (filters.coachId && filters.coachId !== "") {
        const coachIdNum = parseInt(filters.coachId);
        if (!isNaN(coachIdNum) && coachIdNum > 0) {
          params.coachId = coachIdNum;
        }
      }

      // Only add branchId if it's a valid number
      if (filters.branchId && filters.branchId !== "") {
        const branchIdNum = parseInt(filters.branchId);
        if (!isNaN(branchIdNum) && branchIdNum > 0) {
          params.branchId = branchIdNum;
        }
      }

      console.log("Report params:", params);
      const res = await API.get("/admin/reports/coach-revenue", { params });
      setData(res.data);
      setErr("");

      // Show friendly message if no sessions found
      if (res.data && res.data.sessions && res.data.sessions.length === 0) {
        // This is not an error, just no results
        console.log("No sessions found for selected filters");
      }
    } catch (e) {
      console.error("Report error:", e);
      let errorMsg = t('reports.common.failedToLoad');

      if (e.response) {
        errorMsg = apiErrorMessage(e, errorMsg);
      } else if (e.request) {
        errorMsg = t('reports.common.noServerResponse');
      } else {
        errorMsg = apiErrorMessage(e, errorMsg);
      }

      setErr(t('reports.common.errorPrefix', { message: errorMsg }));
      setData(null);
    } finally {
      setLoading(false);
    }
  };


  const role = (getActiveRole() || "ADMIN").toLowerCase();

  const buildExportParams = () => ({
    role,
    from: ddmmyyyyToIso(filters.from),
    to: ddmmyyyyToIso(filters.to),
    branchId: filters.branchId || undefined,
    coachId: filters.coachId || undefined,
  });

  const triggerDownload = async (blobReq, filename) => {
    const res = await blobReq;
    const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = () =>
    triggerDownload(exportCoachRevenueReport({ ...buildExportParams(), format: "pdf" }), "coach-revenue-report.pdf");

  const handleExportCsv = () =>
    triggerDownload(exportCoachRevenueReport({ ...buildExportParams(), format: "csv" }), "coach-revenue-report.csv");

  const handleSendEmail = (email) =>
    emailCoachRevenueReport({ ...buildExportParams(), format: "pdf", toEmail: email });

  const byDayChartData = useMemo(() => {
    if (!data?.byDay?.length) return [];
    return data.byDay.map((row) => {
      const label = row.date === '__UNKNOWN__'
        ? t('reports.common.unknown', { defaultValue: 'Unknown' })
        : row.date;
      const point = { name: label };
      if (row.grossRevenue != null) point.grossRevenue = row.grossRevenue;
      if (row.academyCommission != null) point.academyCommission = row.academyCommission;
      if (row.coachEarnings != null) point.coachEarnings = row.coachEarnings;
      return point;
    });
  }, [data?.byDay, t]);

  const byDaySeries = useMemo(() => {
    const series = [];
    if (byDayChartData.some((r) => r.grossRevenue != null)) {
      series.push({
        dataKey: "grossRevenue",
        name: t("reports.common.grossRevenue", { defaultValue: "Gross revenue" }),
        color: CHART_COLORS.accent,
      });
    }
    if (byDayChartData.some((r) => r.academyCommission != null)) {
      series.push({
        dataKey: "academyCommission",
        name: t("reports.common.academyEarnings", { defaultValue: "Academy" }),
        color: CHART_COLORS.primary,
      });
    }
    if (byDayChartData.some((r) => r.coachEarnings != null)) {
      series.push({
        dataKey: "coachEarnings",
        name: t("reports.common.coachEarnings", { defaultValue: "Coach" }),
        color: CHART_COLORS.warning,
      });
    }
    return series;
  }, [byDayChartData, t]);

  const byCoachChartData = useMemo(() => {
    if (!data?.byCoach?.length) return [];
    return data.byCoach.map((row) => ({
      name: (!row.coachName || row.coachName === '__UNKNOWN__')
        ? t("reports.common.unknown", { defaultValue: "Unknown" })
        : row.coachName,
      value: Number(row.grossRevenue) || 0,
    }));
  }, [data?.byCoach, t]);

  const commissionSplitData = useMemo(() => {
    const s = data?.summary;
    if (!s) return [];
    return [
      {
        name: t("reports.common.academyEarnings", { defaultValue: "Academy" }),
        value: Number(s.totalAcademyCommission) || 0,
        color: CHART_COLORS.primary,
      },
      {
        name: t("reports.common.coachEarnings", { defaultValue: "Coach" }),
        value: Number(s.totalCoachEarnings) || 0,
        color: CHART_COLORS.warning,
      },
    ].filter((d) => d.value > 0);
  }, [data?.summary, t]);

  const chartError = err || null;

  return (
    <div className={embeddedInHub ? "space-y-6" : "list-page"}>
      {!embeddedInHub && (
        <PageHeaderSection
          eyebrow={t('nav.reports')}
          title={t('reports.coachRevenue.title')}
          subtitle={t('reports.coachRevenue.subtitle')}
        />
      )}
      {embeddedInHub && (
        <p className="text-sm text-gray-600">{t('reports.coachRevenue.subtitle')}</p>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {t('reports.common.filters')}
        </h3>
        {filtersLoadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">
            {t('common.optionsLoadFailed')}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4 items-end">
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              📅 {t('reports.common.fromDate')}
            </label>
            <DatePicker
              value={filters.from}
              onChange={(val) => setFilters(f => ({ ...f, from: val }))}
              className="w-full"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              📅 {t('reports.common.toDate')}
            </label>
            <DatePicker
              value={filters.to}
              onChange={(val) => setFilters(f => ({ ...f, to: val }))}
              className="w-full"
            />
          </div>

          <SelectInput
            label={t('reports.common.filterByBranch')}
            value={filters.branchId}
            onChange={e => setFilters(f => ({ ...f, branchId: e.target.value }))}
          >
            <option value="">{t('reports.common.allBranches')}</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </SelectInput>

          <SelectInput
            label={t('reports.common.filterByCoach')}
            value={filters.coachId}
            onChange={e => setFilters(f => ({ ...f, coachId: e.target.value }))}
          >
            <option value="">{t('reports.common.allCoaches')}</option>
            {coaches.map(c => {
              const name = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ') || `#${c.id}`;
              return <option key={c.id} value={c.id}>{name}</option>;
            })}
          </SelectInput>

          <div>
            <ReportGenerateButton onClick={() => generateReport(0)} loading={loading} label={t('reports.common.generateReport')} loadingLabel={t('common.loading')} />
          </div>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {err}
        </div>
      )}

      {hasRun && !loading && byDayChartData.length === 0 && commissionSplitData.length === 0 && byCoachChartData.length === 0 ? (
        <ChartPanel
          title={t("charts.coachRevenueTrend", { defaultValue: "Revenue by day" })}
          empty
          emptyMessage={t("charts.noTrendData", { defaultValue: "No revenue data to chart for this period" })}
        />
      ) : hasRun ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartPanel
            title={t("charts.coachRevenueTrend", { defaultValue: "Revenue by day" })}
            subtitle={t("charts.coachRevenueTrendHint", { defaultValue: "Gross, academy, and coach earnings over the period" })}
            loading={loading}
            empty={!loading && byDayChartData.length === 0}
            emptyMessage={t("charts.noTrendData", { defaultValue: "No daily revenue to chart" })}
            error={chartError}
            height={240}
            className="lg:col-span-2"
          >
            <TrendLine
              data={byDayChartData}
              series={byDaySeries}
              height={220}
              yFormatter={(v) => formatCurrency(v)}
            />
          </ChartPanel>
          <ChartPanel
            title={t("charts.commissionSplit", { defaultValue: "Commission split" })}
            subtitle={t("charts.commissionSplitHint", { defaultValue: "Academy vs coach share" })}
            loading={loading}
            empty={!loading && commissionSplitData.length === 0}
            emptyMessage={t("charts.noSplitData", { defaultValue: "No commission split to chart" })}
            error={chartError}
            height={240}
          >
            <CompositionDonut
              data={commissionSplitData}
              chartSize={180}
              valueFormatter={(v) => formatCurrency(v)}
            />
          </ChartPanel>
          <ChartPanel
            title={t("charts.coachGrossCompare", { defaultValue: "Gross by coach" })}
            subtitle={t("charts.coachGrossCompareHint", { defaultValue: "Compare coach gross revenue" })}
            loading={loading}
            empty={!loading && byCoachChartData.length === 0}
            emptyMessage={t("charts.noCoachData", { defaultValue: "No coach breakdown to chart" })}
            error={chartError}
            height={Math.max(240, 48 + byCoachChartData.length * 36)}
            className="lg:col-span-3"
          >
            <CompareBar
              data={byCoachChartData}
              layout="horizontal"
              height={Math.max(220, byCoachChartData.length * 36)}
              yFormatter={(v) => formatCurrency(v)}
            />
          </ChartPanel>
        </div>
      ) : null}

      {/* Summary Cards */}
      {!loading && data && data.summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-5 text-white">
            <div className="text-xs opacity-90 mb-1">{t('reports.coachRevenue.totalSessions')}</div>
            <div className="text-3xl font-bold">{data.summary.totalSessions}</div>
            <div className="text-xs opacity-75 mt-2">
              {t('reports.coachRevenue.sessionMix', {
                individual: data.summary.totalIndividualSessions,
                batch: data.summary.totalBatchSessions,
              })}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-5 text-white">
            <div className="text-xs opacity-90 mb-1">{t('reports.common.grossRevenue')}</div>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.totalGrossRevenue)}</div>
            <div className="text-xs opacity-75 mt-2">
              {t('reports.common.avg')}: {formatCurrency(data.summary.averageSessionRevenue)}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-5 text-white">
            <div className="text-xs opacity-90 mb-1">{t('reports.common.academyEarnings')}</div>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.totalAcademyCommission)}</div>
            <div className="text-xs opacity-75 mt-2">
              {data.summary.overallCommissionRate.toFixed(1)}% {t('reports.common.average')}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-5 text-white">
            <div className="text-xs opacity-90 mb-1">{t('reports.common.coachEarnings')}</div>
            <div className="text-2xl font-bold">{formatCurrency(data.summary.totalCoachEarnings)}</div>
            <div className="text-xs opacity-75 mt-2">
              {(100 - data.summary.overallCommissionRate).toFixed(1)}% {t('reports.common.average')}
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg shadow-md p-5 text-white flex flex-col justify-center items-center">
            <div className="text-xs opacity-90 mb-2">{t('reports.common.commissionSplit')}</div>
            <div className="text-sm font-bold">
              <div>{t('reports.common.individual')}: {(100 - individualAcademyPct).toFixed(0)}-{(individualAcademyPct).toFixed(0)}</div>
              <div>{t('reports.common.batch')}: {(100 - batchAcademyPct).toFixed(0)}-{(batchAcademyPct).toFixed(0)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Coach-wise & day-wise earnings (full period, not paginated) */}
      {!loading && data && ((data.byCoach && data.byCoach.length > 0) || (data.byDay && data.byDay.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.byCoach && data.byCoach.length > 0 && (
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b bg-slate-50 font-semibold text-slate-800">
                {t('reports.coachRevenue.coachWiseEarnings')}
              </div>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">{t('reports.common.coach')}</th>
                      <th className="px-3 py-2 text-right">{t('reports.common.sessions')}</th>
                      <th className="px-3 py-2 text-right">{t('reports.common.gross')}</th>
                      <th className="px-3 py-2 text-right">{t('reports.common.academy')}</th>
                      <th className="px-3 py-2 text-right">{t('reports.common.coach')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byCoach.map((row) => (
                      <tr key={row.coachId ?? row.coachName} className="border-t">
                        <td className="px-3 py-2">{row.coachName || t('reports.common.unknown')}</td>
                        <td className="px-3 py-2 text-right">{row.sessions}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(row.grossRevenue)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(row.academyCommission)}</td>
                        <td className="px-3 py-2 text-right font-medium text-orange-700">{formatCurrency(row.coachEarnings)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data.byDay && data.byDay.length > 0 && (
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-800">{t('reports.coachRevenue.dayWiseEarnings')}</span>
                <span className="text-xs font-normal text-slate-500 whitespace-nowrap">
                  {data.byDay.length > 8
                    ? t('reports.coachRevenue.daysScrollHint', { count: data.byDay.length })
                    : t('reports.coachRevenue.daysCount', { count: data.byDay.length })}
                </span>
              </div>
              {/* Avoid `overflow-x-auto`: global CSS sets overflow-y:hidden on it and clipped this panel. */}
              <div className="max-h-80 overflow-auto overscroll-contain">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 sticky top-0 z-[1]">
                    <tr>
                      <th className="px-3 py-2 bg-gray-50">{t('reports.common.date')}</th>
                      <th className="px-3 py-2 text-right bg-gray-50">{t('reports.common.sessions')}</th>
                      <th className="px-3 py-2 text-right bg-gray-50">{t('reports.common.gross')}</th>
                      <th className="px-3 py-2 text-right bg-gray-50">{t('reports.common.academy')}</th>
                      <th className="px-3 py-2 text-right bg-gray-50">{t('reports.common.coach')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byDay.map((row) => (
                      <tr key={row.date} className="border-t">
                        <td className="px-3 py-2">{row.date === '__UNKNOWN__' ? t('reports.common.unknown', { defaultValue: 'Unknown' }) : row.date}</td>
                        <td className="px-3 py-2 text-right">{row.sessions}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(row.grossRevenue)}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(row.academyCommission)}</td>
                        <td className="px-3 py-2 text-right font-medium text-orange-700">{formatCurrency(row.coachEarnings)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <ListLoadingSpinner label={t('reports.common.loadingReport')} />
      )}

      {/* No Results */}
      {!loading && hasRun && data && data.sessions && data.sessions.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-6 py-8 rounded-lg text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-xl font-medium">{t('reports.coachRevenue.noSessionsFound')}</p>
          <p className="text-sm mt-2">{t('reports.coachRevenue.noSessionsHint')}</p>
        </div>
      )}

      {/* Sessions Table */}
      {!loading && data && data.sessions && data.sessions.length > 0 && (
        <>
          <div
            ref={resultsRef}
            className="bg-white rounded-lg shadow-sm border overflow-hidden scroll-mt-24"
          >
            <div className="px-6 py-4 bg-gray-50 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{t('reports.common.sessionDetails')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('reports.common.showingSessionsPage', {
                  count: data.sessions.length,
                  total: data.totalRecords,
                  page: data.currentPage + 1,
                  pages: data.totalPages,
                })}
              </p>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-2">
                <ExcelDownloadButton
                  columns={[
                    { header: t('reports.common.sessionId'), key: 'sessionId', width: 10 },
                    { header: t('reports.common.date'), key: 'sessionDate', width: 14, render: (v) => formatDate(v) || v },
                    { header: t('reports.common.startTime'), key: 'startTime', width: 12, render: (v) => formatTime(v) || v },
                    { header: t('reports.common.endTime', { defaultValue: 'End' }), key: 'endTime', width: 12, render: (v) => formatTime(v) || v },
                    { header: t('reports.common.endTime'), key: 'endTime', width: 12 },
                    { header: t('reports.common.type'), key: 'sessionType', width: 14, render: (v) => (v === 'INDIVIDUAL' ? t('reports.common.individualSession') : t('reports.common.batchSession', { defaultValue: 'Batch' })) },
                    { header: t('reports.common.batch'), key: 'batchName', render: (v) => localizeBatchName(v, t) || t('reports.common.individual'), width: 20 },
                    { header: t('reports.common.branch'), key: 'branchName', render: (v) => v || '', width: 18 },
                    { header: t('reports.common.coach'), key: 'coachName', render: (v) => v || '', width: 22 },
                    { header: t('reports.common.topic'), key: 'topic', width: 28 },
                    { header: t('reports.common.present'), key: 'presentCount', width: 14 },
                    { header: t('reports.common.grossRevenue'), key: 'grossRevenue', render: (v) => formatCurrency(v || 0), width: 14 },
                    { header: t('reports.common.academyShare'), key: 'academyCommission', render: (v) => formatCurrency(v || 0), width: 14 },
                    { header: t('reports.common.coachEarnings'), key: 'coachEarnings', render: (v) => formatCurrency(v || 0), width: 14 },
                  ]}
                  data={data.sessions || []}
                  filename="coach-revenue-report"
                  disabled={!data || !data.sessions || data.sessions.length === 0}
                  label={t('reports.common.downloadPageExcel')}
                />
                <p className="text-xs text-gray-500 max-w-xs text-right">
                  {t('reports.common.exportFullHint')}
                </p>
              </div>
            </div>

            <div className="md:hidden p-3 space-y-3">
              {data.sessions.map((session) => {
                const isIndividual = session.sessionType === 'INDIVIDUAL';
                const academyPct = isIndividual ? individualAcademyPct : batchAcademyPct;
                const commissionPercent = `${academyPct}%`;
                const coachPercent = `${(100 - academyPct).toFixed(0)}%`;

                return (
                  <article
                    key={session.sessionId}
                    className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          {displaySessionDate(session)}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {displaySessionTime(session.startTime)} – {displaySessionTime(session.endTime)}
                        </p>
                        <p className="mt-0.5 text-sm text-gray-700 truncate">
                          {isIndividual ? t('reports.common.individualSession') : (localizeBatchName(session.batchName, t) || "—")}
                          {session.branchName ? ` · ${session.branchName}` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-blue-100 transition-colors relative cursor-pointer flex-shrink-0"
                        onClick={() => setClickedSession(session.sessionId)}
                        title={t('reports.common.viewAttendance')}
                      >
                        <span className="text-lg">👥</span>
                        <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                          {session.presentCount}
                        </span>
                      </button>
                    </div>
                    {session.topic ? (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{session.topic}</p>
                    ) : null}
                    <dl className="grid grid-cols-3 gap-x-2 gap-y-2 text-sm border-t border-gray-100 pt-3">
                      <div className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.gross')}</dt>
                        <dd className="font-bold text-gray-900 text-xs sm:text-sm">{formatCurrency(session.grossRevenue)}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.academy')} ({commissionPercent})</dt>
                        <dd className="font-bold text-purple-700 text-xs sm:text-sm">{formatCurrency(session.academyCommission)}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.coach')} ({coachPercent})</dt>
                        <dd className="font-bold text-orange-700 text-xs sm:text-sm">{formatCurrency(session.coachEarnings)}</dd>
                      </div>
                    </dl>
                    {clickedSession === session.sessionId && (
                      <AttendancePopup
                        session={session}
                        onClose={() => setClickedSession(null)}
                      />
                    )}
                  </article>
                );
              })}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 space-y-2">
                <p className="text-sm font-semibold text-gray-700">{t('reports.common.periodTotalAllPages')}</p>
                <dl className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.gross')}</dt>
                    <dd className="font-bold text-gray-900 text-xs">{formatCurrency(data.summary.totalGrossRevenue)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.academy')}</dt>
                    <dd className="font-bold text-purple-700 text-xs">{formatCurrency(data.summary.totalAcademyCommission)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.coach')}</dt>
                    <dd className="font-bold text-orange-700 text-xs">{formatCurrency(data.summary.totalCoachEarnings)}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.dateAndTime')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.batchAndBranch')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.sessionNotes')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.students')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.grossRevenue')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.academyShare')}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('reports.common.coachEarnings')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.sessions.map((session) => {
                    const isIndividual = session.sessionType === 'INDIVIDUAL';
                    const academyPct = isIndividual ? individualAcademyPct : batchAcademyPct;
                    const commissionPercent = `${academyPct}%`;
                    const coachPercent = `${(100 - academyPct).toFixed(0)}%`;

                    return (
                      <tr key={session.sessionId} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-center align-top whitespace-nowrap">
                          <SessionDateTimeDisplay
                            startTime={session.startTime}
                            endTime={session.endTime}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isIndividual ? (
                            <div className="text-sm text-gray-600 italic">{t('reports.common.individualSession')}</div>
                          ) : (
                            session.batchName && (
                              <div className="text-sm text-gray-900">{localizeBatchName(session.batchName, t)}</div>
                            )
                          )}
                          {session.branchName && (
                            <div className="text-xs text-gray-500 mt-1">{session.branchName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-sm text-gray-900" title={session.topic}>
                            {session.topic}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center relative">
                          <button
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-blue-100 transition-colors relative cursor-pointer"
                            onClick={() => setClickedSession(session.sessionId)}
                            title={t('reports.common.clickViewAttendance')}>
                            <span className="text-lg">👥</span>
                            <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                              {session.presentCount}
                            </span>
                          </button>
                          {clickedSession === session.sessionId && (
                            <AttendancePopup
                              session={session}
                              onClose={() => setClickedSession(null)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{formatCurrency(session.grossRevenue)}</div>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <div className="text-sm font-bold text-purple-700">{formatCurrency(session.academyCommission)}</div>
                          <div className="text-xs text-gray-500">{commissionPercent}</div>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <div className="text-sm font-bold text-orange-700">{formatCurrency(session.coachEarnings)}</div>
                          <div className="text-xs text-gray-500">{coachPercent}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan="4" className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                      {t('reports.common.periodTotalAllPages')}:
                    </td>
                    <td className="px-4 py-3 text-center text-base font-bold text-gray-900">
                      {formatCurrency(data.summary.totalGrossRevenue)}
                    </td>
                    <td className="px-4 py-3 text-center text-base font-bold text-purple-700">
                      {formatCurrency(data.summary.totalAcademyCommission)}
                    </td>
                    <td className="px-4 py-3 text-center text-base font-bold text-orange-700">
                      {formatCurrency(data.summary.totalCoachEarnings)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data && data.totalRecords > 0 && (
            <div className="bg-white px-6 py-4 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    {t('reports.common.showingSessionsRange', {
                      start: (data.currentPage * pageSize) + 1,
                      end: (data.currentPage * pageSize) + data.sessions.length,
                      total: data.totalRecords,
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <SelectInput
                      label={t('reports.common.pageSize')}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value));
                        setCurrentPage(0);
                      }}
                      className="text-sm"
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </SelectInput>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {t('reports.common.pageOf', { page: data.currentPage + 1, pages: data.totalPages })}
                </div>
              </div>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => generateReport(0)}
                  disabled={data.currentPage === 0}
                  className="px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
                  {t('reports.common.first')}
                </button>
                <button
                  onClick={() => generateReport(data.currentPage - 1)}
                  disabled={data.currentPage === 0}
                  className="px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
                  {t('reports.common.previous')}
                </button>
                <span className="px-4 py-2 border rounded-md text-sm font-medium bg-blue-50 text-blue-700">
                  {data.currentPage + 1}
                </span>
                <button
                  onClick={() => generateReport(data.currentPage + 1)}
                  disabled={data.currentPage >= data.totalPages - 1}
                  className="px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
                  {t('reports.common.next')}
                </button>
                <button
                  onClick={() => generateReport(data.totalPages - 1)}
                  disabled={data.currentPage >= data.totalPages - 1}
                  className="px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
                  {t('reports.common.last')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Export & Email */}
      <ReportExportBar
        disabled={!hasRun || !data}
        onExportPdf={() => {
          const role = (getActiveRole() || 'ADMIN').toLowerCase();
          const params = {
            role,
            from: ddmmyyyyToIso(filters.from),
            to: ddmmyyyyToIso(filters.to),
            branchId: filters.branchId || undefined,
            coachId: filters.coachId || undefined,
            format: 'pdf',
          };
          return exportCoachRevenueReport(params).then(res => {
            const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'coach-revenue-report.pdf';
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); window.URL.revokeObjectURL(url);
          });
        }}
        onExportCsv={() => {
          const role = (getActiveRole() || 'ADMIN').toLowerCase();
          const params = {
            role,
            from: ddmmyyyyToIso(filters.from),
            to: ddmmyyyyToIso(filters.to),
            branchId: filters.branchId || undefined,
            coachId: filters.coachId || undefined,
            format: 'csv',
          };
          return exportCoachRevenueReport(params).then(res => {
            const blob = new Blob([res.data], { type: res.headers['content-type'] || 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'coach-revenue-report.csv';
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); window.URL.revokeObjectURL(url);
          });
        }}
        onSendEmail={(email) => {
          const role = (getActiveRole() || 'ADMIN').toLowerCase();
          return emailCoachRevenueReport({
            role,
            from: ddmmyyyyToIso(filters.from),
            to: ddmmyyyyToIso(filters.to),
            branchId: filters.branchId || undefined,
            coachId: filters.coachId || undefined,
            format: 'pdf',
            toEmail: email,
          });
        }}
        emailLabel={t('reports.common.enterEmail')}
      />
    </div>
  );
}
